import { formatDate, formatMoney, t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { type ReactElement, useState } from "react";

import type { DoorResult } from "../session/useSession.js";

/**
 * `round T-2b`, `M1 (d)` (`P2-DLW-008`; WF-DLR-07 steps 1 to 4): **the dealer's step at the
 * control , the checklist, the advance the door will debit, the confirmation by his own door.**
 *
 * `GET /api/v1/me/vehicles/{id}/availability` is the card's one read: the scheduled control (its
 * Thursday, its cabinet) or the waiting sentence; the advance due DERIVED by the door (0 once
 * settled, the reduced figure when a valid report stands, else the control's one price , the
 * amount shown IS the amount debited, the item's own test); the balance. « Confirmer la mise à
 * disposition » live, or greyed with the guard's EXACT reason (« solde de X FCFA pour une avance de
 * Y FCFA ») and « Recharger mon solde » beside it (T-1d's declaration door); once made available,
 * the panel only says: waiting for the cabinet's passage and its report, the verdict computed by
 * Hakili's grid (step 4).
 */
export interface AvailabilityView {
  readonly demandId: string | null;
  readonly scheduled: {
    readonly dayLabel: string;
    readonly dayOn: string;
    readonly cabinetName: string;
  } | null;
  readonly advanceDue: number | null;
  readonly advanceReduced: boolean;
  readonly advanceSettled: boolean;
  readonly balance: number;
  readonly blockedReason: string | null;
  readonly madeAvailableAt: string | null;
  readonly reportReceived: boolean;
  /** `PRICE-GAP-DEALER`, M4: the report is the standing one the file was attached to, not a passage made for it. */
  readonly reportReused: boolean;
  /** `D5`: true on a buyer's control , no advance, no balance guard, the buyer's own charge. */
  readonly buyerPaysControl: boolean;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);
const rec = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

export function availabilityFrom(answer: unknown): AvailabilityView | null {
  const value = rec(answer);
  if (value === null) return null;
  const scheduled = rec(value["scheduled"]);
  const dayLabel = str(scheduled?.["dayLabel"]);
  const dayOn = str(scheduled?.["dayOn"]);
  const cabinetName = str(scheduled?.["cabinetName"]);
  return {
    demandId: str(value["demandId"]),
    scheduled:
      dayLabel === null || dayOn === null || cabinetName === null ? null : { dayLabel, dayOn, cabinetName },
    advanceDue: num(value["advanceDue"]),
    advanceReduced: value["advanceReduced"] === true,
    advanceSettled: value["advanceSettled"] === true,
    balance: num(value["balance"]) ?? 0,
    blockedReason: str(value["blockedReason"]),
    madeAvailableAt: str(value["madeAvailableAt"]),
    reportReceived: value["reportReceived"] === true,
    reportReused: value["reportReused"] === true,
    buyerPaysControl: value["buyerPaysControl"] === true,
  };
}

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

export function AvailabilityCard(props: {
  readonly view: AvailabilityView;
  readonly confirm: () => Promise<DoorResult<unknown>>;
  readonly onChanged?: () => void;
  readonly naviguer: (to: string) => void;
}): ReactElement | null {
  const { view } = props;
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  if (view.demandId === null) return null;
  const made = view.madeAvailableAt !== null;
  const madeAvailableAt = view.madeAvailableAt ?? "";
  const press = (): void => {
    setBusy(true);
    setRefusal(null);
    void props.confirm().then((answer) => {
      setBusy(false);
      if (answer.ok) props.onChanged?.();
      else setRefusal(answer.message);
    });
  };
  return (
    <section className="mt-4 rounded-2xl border border-gray-200 bg-white p-4" data-testid="vehicule-controle">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-gray-900">{t("dealer.controle.titre")}</h3>
        <span className="text-xs text-gray-500">{t("dealer.controle.sous")}</span>
      </div>
      {view.reportReused ? (
        // `PRICE-GAP-DEALER`, M4 (V-005): a file attached to a standing report asks for no passage , no Thursday,
        // no advance, no confirmation , and says which report covers it.
        <>
          <p className="mt-3 text-sm leading-relaxed text-gray-700" data-testid="vehicule-controle-couvert">
            {t("dealer.controle.couvert")}
          </p>
          <ul className="mt-3 space-y-1.5 text-sm text-gray-700" data-testid="vehicule-controle-liste">
            <li data-testid="vehicule-controle-rapport">{t("dealer.controle.rapportRecu")}</li>
          </ul>
        </>
      ) : (
        <>
          {/* WF-DLR-07 step 1: the checklist as facts. */}
          <ul className="mt-3 space-y-1.5 text-sm text-gray-700" data-testid="vehicule-controle-liste">
            <li data-testid="vehicule-controle-programme">
              {view.scheduled === null
                ? t("dealer.controle.enAttenteProgrammation")
                : fill("dealer.controle.programmeLe", {
                    jour: view.scheduled.dayLabel,
                    cabinet: view.scheduled.cabinetName,
                  })}
            </li>
            <li data-testid="vehicule-controle-avance">
              {view.buyerPaysControl
                ? t("dealer.controle.avancePayeeParAcheteur")
                : view.advanceSettled
                  ? t("dealer.controle.avanceReglee")
                  : view.advanceDue === null
                    ? t("dealer.controle.avanceNonPubliee")
                    : fill(
                        view.advanceReduced
                          ? "dealer.controle.avanceDueReduite"
                          : "dealer.controle.avanceDue",
                        { montant: formatMoney(view.advanceDue) },
                      )}
            </li>
            <li data-testid="vehicule-controle-disposition">
              {made
                ? fill("dealer.controle.miseADispositionLe", { date: formatDate(new Date(madeAvailableAt)) })
                : t("dealer.controle.miseADispositionAttendue")}
            </li>
            <li data-testid="vehicule-controle-rapport">
              {view.reportReceived ? t("dealer.controle.rapportRecu") : t("dealer.controle.rapportAttendu")}
            </li>
          </ul>
          {made ? (
            // Step 4: once made available, the panel only waits , and stops waiting the moment the report is here
            // (V-005 read « Rapport du cabinet reçu » over « En attente du passage … et de son rapport »).
            view.reportReceived ? (
              <p
                className="mt-3 text-sm leading-relaxed text-gray-600"
                data-testid="vehicule-controle-verdict"
              >
                {t("dealer.controle.verdictCalcule")}
              </p>
            ) : (
              <p
                className="mt-3 text-sm leading-relaxed text-gray-600"
                data-testid="vehicule-controle-attente"
              >
                {t("dealer.controle.attenteCabinet")}
              </p>
            )
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {view.blockedReason === null ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={press}
                  data-testid="vehicule-controle-confirmer"
                  className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                >
                  {t("dealer.controle.confirmer")}
                </button>
              ) : (
                <>
                  <GuardedButton
                    disabled
                    reason={view.blockedReason}
                    variant="primary"
                    testId="vehicule-controle-confirmer"
                  >
                    {t("dealer.controle.confirmer")}
                  </GuardedButton>
                  {/* The guard's shortcut: the top-up declared by the dealer's own door (T-1d). */}
                  <button
                    type="button"
                    onClick={() => {
                      props.naviguer("/solde");
                    }}
                    data-testid="vehicule-controle-recharger"
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700"
                  >
                    {t("dealer.controle.recharger")}
                  </button>
                </>
              )}
              {refusal === null ? null : (
                <p
                  role="alert"
                  data-testid="vehicule-controle-refus"
                  className="w-full text-sm text-rose-600"
                >
                  {refusal}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
